import { useState } from 'react';
import { Alert, Button, Divider, Group, Loader, Paper, Stack, Table, Text, Textarea, TextInput, Title, Tooltip } from '@mantine/core';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { StockAdjustmentResponse, StockAdjustmentUpdate } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { LocationName, ProductName, SiteName } from '../../../features/stores-transfers/components/EntityNames';
import { SiteSelect } from '../../../features/stores-transfers/components/SiteSelect';
import { LedgerPreview } from '../../../features/stores-transfers/components/LedgerPreview';
import { LineRowsEditor, type LineFormRow } from '../../../features/stores-transfers/components/LineRowsEditor';
import { formatQty } from '../../../features/stores-transfers/format';
import {
  useAdjustment,
  useCancelAdjustment,
  useCreateAdjustment,
  usePostAdjustment,
  useReverseAdjustment,
  useUpdateAdjustment,
} from '../../../features/stores-transfers/api';

/** Owned by G-A (Wave HOWICK-1). Stock-adjustment detail: editable while DRAFT
 * (edit lines + Post, or Cancel — the one lifecycle branch stores issues have no
 * precedent for), read-only once POSTED with a "Reverse & clone to draft" action
 * once REVERSED per §4.1, and a plain terminal message once CANCELLED. */
export const Route = createFileRoute('/_authenticated/stores-transfers/adjustments/$adjustmentId')({
  component: AdjustmentDetailPage,
});

function AdjustmentDetailPage() {
  const { adjustmentId } = Route.useParams();
  const { data: adjustment, isLoading, isError } = useAdjustment(adjustmentId);

  if (isLoading) {
    return <Loader />;
  }
  if (isError || !adjustment) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Stock adjustment not found.
      </Alert>
    );
  }

  return adjustment.status === 'DRAFT' ? <DraftAdjustmentEditor key={adjustment.id} adjustment={adjustment} /> : <PostedAdjustmentView adjustment={adjustment} />;
}

function toLineFormRows(adjustment: StockAdjustmentResponse): LineFormRow[] {
  return adjustment.lines.map((line) => ({
    key: line.id,
    productId: line.productId,
    productLabel: '',
    locationId: line.locationId,
    locationLabel: '',
    qty: line.qtyDelta,
  }));
}

function DraftAdjustmentEditor({ adjustment }: { adjustment: StockAdjustmentResponse }) {
  const canOperate = useCanOperate();
  const navigate = useNavigate();
  const updateAdjustment = useUpdateAdjustment(adjustment.id);
  const postAdjustment = usePostAdjustment(adjustment.id);
  const cancelAdjustment = useCancelAdjustment(adjustment.id);

  const [siteId, setSiteId] = useState(adjustment.siteId);
  const [reason, setReason] = useState(adjustment.reason);
  const [notes, setNotes] = useState(adjustment.notes ?? '');
  const [lines, setLines] = useState<LineFormRow[]>(() => toLineFormRows(adjustment));
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const linesValid = lines.length > 0 && lines.every((l) => l.productId && l.locationId && l.qty && Number(l.qty) !== 0);

  function buildPayload(): StockAdjustmentUpdate {
    return {
      siteId,
      reason: reason.trim(),
      notes: notes.trim() || null,
      lines: lines.map((l) => ({ productId: l.productId, locationId: l.locationId, qtyDelta: l.qty })),
    };
  }

  async function handleSave() {
    setError(null);
    try {
      await updateAdjustment.mutateAsync(buildPayload());
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to save changes.');
    }
  }

  async function handlePost() {
    setError(null);
    try {
      await updateAdjustment.mutateAsync(buildPayload());
      await postAdjustment.mutateAsync(undefined);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to post the adjustment.');
    }
  }

  async function handleCancel() {
    setError(null);
    setCancelling(true);
    try {
      await cancelAdjustment.mutateAsync(undefined);
      void navigate({ to: '/stores-transfers' });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to cancel this adjustment.');
    } finally {
      setCancelling(false);
    }
  }

  return (
    <Stack gap="lg" maw={860}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{adjustment.number}</Title>
          <DocStatusBadge status={adjustment.status} />
        </Group>
        <Button component={Link} to="/stores-transfers" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="md">
          <SiteSelect label="Site" value={siteId} onChange={setSiteId} required />
          <TextInput label="Reason" value={reason} onChange={(event) => setReason(event.currentTarget.value)} required />
          <Textarea label="Notes" value={notes} onChange={(event) => setNotes(event.currentTarget.value)} autosize minRows={2} />
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <LineRowsEditor rows={lines} onChange={setLines} siteId={siteId || undefined} includeLocation locationLabel="Location" />
      </Paper>

      <Paper withBorder radius="md" p="md">
        <LedgerPreview siteId={siteId || undefined} lines={lines} attribution={`Reason: ${reason}`} />
      </Paper>

      <Group justify="space-between">
        <Tooltip label="Requires Operations role" disabled={canOperate}>
          <span>
            <ConfirmButton
              color="red"
              variant="light"
              disabled={!canOperate}
              loading={cancelling}
              label="Cancel adjustment"
              message={`Cancel draft adjustment ${adjustment.number}? This has no ledger effect — nothing was posted yet.`}
              onConfirm={handleCancel}
            />
          </span>
        </Tooltip>
        <Group>
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <Button variant="default" disabled={!canOperate} onClick={handleSave} loading={updateAdjustment.isPending && !postAdjustment.isPending}>
                Save changes
              </Button>
            </span>
          </Tooltip>
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <Button onClick={handlePost} loading={postAdjustment.isPending} disabled={!canOperate || !linesValid}>
                Post
              </Button>
            </span>
          </Tooltip>
        </Group>
      </Group>
    </Stack>
  );
}

function PostedAdjustmentView({ adjustment }: { adjustment: StockAdjustmentResponse }) {
  const canWrite = useCanOperate();
  const navigate = useNavigate();
  const reverseAdjustment = useReverseAdjustment(adjustment.id);
  const createAdjustment = useCreateAdjustment();
  const [error, setError] = useState<string | null>(null);
  const [reversing, setReversing] = useState(false);

  async function handleReverseAndClone() {
    setError(null);
    setReversing(true);
    try {
      await reverseAdjustment.mutateAsync(undefined);
      // "Clone" half of "reverse & clone to draft" (BUILD_SPEC.md §4.1) — see the
      // matching comment on the stores-issue detail screen for why the
      // just-reversed document's own header/lines are exactly what to prefill.
      const clone = await createAdjustment.mutateAsync({
        siteId: adjustment.siteId,
        reason: adjustment.reason,
        notes: adjustment.notes,
        lines: adjustment.lines.map((line) => ({
          productId: line.productId,
          locationId: line.locationId,
          qtyDelta: line.qtyDelta,
          handlingUnitsDelta: line.handlingUnitsDelta,
          note: line.note,
        })),
      });
      void navigate({ to: '/stores-transfers/adjustments/$adjustmentId', params: { adjustmentId: clone.id } });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this adjustment.');
    } finally {
      setReversing(false);
    }
  }

  return (
    <Stack gap="lg" maw={860}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{adjustment.number}</Title>
          <DocStatusBadge status={adjustment.status} />
        </Group>
        <Button component={Link} to="/stores-transfers" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group gap={6}>
            <Text fw={600}>Site:</Text>
            <SiteName id={adjustment.siteId} />
          </Group>
          <Group gap={6}>
            <Text fw={600}>Reason:</Text>
            <Text>{adjustment.reason}</Text>
          </Group>
          {adjustment.notes && (
            <Group gap={6} align="flex-start">
              <Text fw={600}>Notes:</Text>
              <Text>{adjustment.notes}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>Location</Table.Th>
              <Table.Th>Qty delta</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {adjustment.lines.map((line) => (
              <Table.Tr key={line.id}>
                <Table.Td>
                  <ProductName id={line.productId} />
                </Table.Td>
                <Table.Td>
                  <LocationName id={line.locationId} />
                </Table.Td>
                <Table.Td>{formatQty(line.qtyDelta)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {adjustment.status === 'POSTED' && (
        <>
          <Divider />
          <Group justify="flex-end">
            <Tooltip label="Requires Operations role" disabled={canWrite}>
              <span>
                <ConfirmButton
                  color="amber"
                  variant="light"
                  disabled={!canWrite}
                  loading={reversing}
                  label="Reverse & clone to draft"
                  message={`Reverse stock adjustment ${adjustment.number}? Its stock will be backed out by a reversing ledger entry, and a new DRAFT adjustment will be created for you to redo.`}
                  onConfirm={handleReverseAndClone}
                />
              </span>
            </Tooltip>
          </Group>
        </>
      )}
      {adjustment.status === 'REVERSED' && (
        <Text c="dimmed" size="sm">
          This adjustment has been reversed. Its ledger effect has been undone; see the register for any draft that
          superseded it.
        </Text>
      )}
      {adjustment.status === 'CANCELLED' && (
        <Text c="dimmed" size="sm">
          This adjustment was cancelled before it was posted — it has no ledger effect.
        </Text>
      )}
    </Stack>
  );
}
